import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {environment} from '@env/environment';
import {LocalStorageService} from '@services/local-storage.service';
import {BankAccountService} from '@services/bank-account.service';
import {BankAccountListModel} from '@models/bank-account-list.model';
import {TAccountingPlatform} from '@shared/interface';
import {
  IConnectionHelpCase,
  IChatFeedbackRequest,
  IChatPageContext,
  IChatSendRequest,
  IChatSendResponse,
  IChatThread,
  IChatThreadListItem,
  TChatPage,
} from './chatbot.interface';

// Path prefix each accounting platform's rules pages live under - matches resolvePage()'s
// RULES_ENGINE detection below.
const RULES_ENGINE_PLATFORM_BY_PATH: Record<string, TAccountingPlatform> = {
  '/quickbooks': 'quickbooks',
  '/zohobooks': 'zohobooks',
  '/manual': 'manual',
};

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly baseUrl = `${environment.API_URL}/assistant/chat`;

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly bankAccountService = inject(BankAccountService);

  // Asks the header chat to open straight on a connection-help case thread.
  readonly helpCaseOpenRequested = new Subject<IConnectionHelpCase>();

  openHelpCase(helpCase: IConnectionHelpCase): void {
    this.helpCaseOpenRequested.next(helpCase);
  }

  listThreads(): Observable<IChatThreadListItem[]> {
    return this.http.get<IChatThreadListItem[]>(`${this.baseUrl}/threads`);
  }

  getThread(threadId: number): Observable<IChatThread> {
    return this.http.get<IChatThread>(`${this.baseUrl}/threads/${threadId}`);
  }

  sendMessage(threadId: number | null, message: string): Observable<IChatSendResponse> {
    const payload: IChatSendRequest = {
      threadId,
      message,
      pageContext: this.buildPageContext(),
    };

    const selectedClientId = this.localStorageService.getSelectedClient()?.id;
    if (!!selectedClientId) {
      payload.selectedClientId = selectedClientId;
    }

    return this.http.post<IChatSendResponse>(this.baseUrl, payload);
  }

  sendFeedback(messageId: number, feedback: IChatFeedbackRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/messages/${messageId}/feedback`, feedback);
  }

  private buildPageContext(): IChatPageContext {
    const url = this.router.url.split('?')[0];
    const page = this.resolvePage(url);
    const pageContext: IChatPageContext = {page};

    // The selected connection is only meaningful on the Bank Connections page.
    if (page === 'CLIENT_BANKS') {
      const bank = this.getSelectedBankAccount();
      if (bank?.id) {
        pageContext.institution = bank.name;
        pageContext.bankAccountId = bank.id;
        pageContext.source = bank.source;
      }
    }

    // The accounting platform is only meaningful on the rules-engine pages.
    if (page === 'RULES_ENGINE') {
      pageContext.platform = this.resolvePlatform(url);
    }

    return pageContext;
  }

  private resolvePage(url: string): TChatPage {
    if (url.startsWith('/banks/connections')) {
      return 'CLIENT_BANKS';
    }
    if (url.startsWith('/quickbooks-close')) {
      return 'LS_CLOSE';
    }
    if (Object.keys(RULES_ENGINE_PLATFORM_BY_PATH).some((path) => url === path || url.startsWith(`${path}/`))) {
      return 'RULES_ENGINE';
    }
    return 'OTHER';
  }

  private resolvePlatform(url: string): TAccountingPlatform | undefined {
    const path = Object.keys(RULES_ENGINE_PLATFORM_BY_PATH)
      .find((candidate) => url === candidate || url.startsWith(`${candidate}/`));
    return path ? RULES_ENGINE_PLATFORM_BY_PATH[path] : undefined;
  }

  // The subject is a ReplaySubject(1), so take(1) yields the last selected account synchronously.
  private getSelectedBankAccount(): BankAccountListModel | null {
    let bank: BankAccountListModel | null = null;
    this.bankAccountService.selectedBankAccount.pipe(take(1)).subscribe((value) => bank = value);
    return bank;
  }
}
